import * as TForm from "../../models/TForm";
import { TActionObservable, TFormActionPayload } from "../../state/applyActions";
import * as TSetState from "../../state/TSetState";
import * as TGetState from "../../state/TGetState";
import { TState } from "./lensBaseTypes";
import { reduceDataToStateUpdate } from "./reduceDataToStateUpdate";
import { set } from "./set";
import * as rxjs from "rxjs";
import * as rxjsOperators from "rxjs/operators";
import { TPayloadActionType } from "../../state/actions/TAction";

interface ILensSetForm<A extends TPayloadActionType, T, S> {
    // 5 level lens path guard
    <
        K1 extends keyof S,
        K2 extends keyof S[K1],
        K3 extends keyof S[K1][K2],
        K4 extends keyof S[K1][K2][K3],
        K5 extends keyof S[K1][K2][K3][K4]
    >
    (
        path: [K1, K2, K3, K4, K5],
    ): (
        obs$: TActionObservable<A, TFormActionPayload<Required<TForm.TUnpackEditFormType<T>>, keyof TForm.TUnpackEditFormType<T>>>,
        getState: TGetState.TGetState,
        setState: TSetState.TSetState
    ) => rxjs.Observable<unknown>;
    // 4 level lens path guard
    <
        K1 extends keyof S,
        K2 extends keyof S[K1],
        K3 extends keyof S[K1][K2],
        K4 extends keyof S[K1][K2][K3]
    >
    (
        path: [K1, K2, K3, K4],
    ): (
        obs$: TActionObservable<A, TFormActionPayload<Required<TForm.TUnpackEditFormType<T>>, keyof TForm.TUnpackEditFormType<T>>>,
        getState: TGetState.TGetState,
        setState: TSetState.TSetState
    ) => rxjs.Observable<unknown>;
    // 3 level lens path guard
    <
        K1 extends keyof S,
        K2 extends keyof S[K1],
        K3 extends keyof S[K1][K2]
    >
    (
        path: [K1, K2, K3],
    ): (
        obs$: TActionObservable<A, TFormActionPayload<Required<TForm.TUnpackEditFormType<T>>, keyof TForm.TUnpackEditFormType<T>>>,
        getState: TGetState.TGetState,
        setState: TSetState.TSetState
    ) => rxjs.Observable<unknown>;
    // 2 level lens path guard
    <
        K1 extends keyof S,
        K2 extends keyof S[K1]
    >
    (
        path: [K1, K2],
    ): (
        obs$: TActionObservable<A, TFormActionPayload<Required<TForm.TUnpackEditFormType<T>>, keyof TForm.TUnpackEditFormType<T>>>,
        getState: TGetState.TGetState,
        setState: TSetState.TSetState
    ) => rxjs.Observable<unknown>;
    // 1 level lens path guard
    <K1 extends keyof S>
    (
        path: [K1],
    ): (
        obs$: TActionObservable<A, TFormActionPayload<Required<TForm.TUnpackEditFormType<T>>, keyof TForm.TUnpackEditFormType<T>>>,
        getState: TGetState.TGetState,
        setState: TSetState.TSetState
    ) => rxjs.Observable<unknown>;
}

export const setForm =
    <A extends TPayloadActionType, T>(): ILensSetForm<A, T, TState> =>
        // There is no way to create paramater overloads here so we set to any
        (lens: any) => // eslint-disable-line
            (
                obs$: TActionObservable<A, TFormActionPayload<Required<TForm.TUnpackEditFormType<T>>, keyof TForm.TUnpackEditFormType<T>>>,
                getState: TGetState.TGetState,
                setState: TSetState.TSetState
            ) =>
                obs$.pipe(
                    rxjsOperators.tap((action) =>
                        reduceDataToStateUpdate<TFormActionPayload<Required<TForm.TUnpackEditFormType<T>>, keyof TForm.TUnpackEditFormType<T>>>(setState)(
                            set<TFormActionPayload<Required<TForm.TUnpackEditFormType<T>>, keyof TForm.TUnpackEditFormType<T>>>()(
                                lens,
                                /* eslint-disable */
                                // The type is always expected to be never for the form, since we have abstraced the lens scoping,
                                // which is untrue
                                // @ts-ignore
                                (form, payload) => TForm.updateFromAction2<T>(form, payload)
                                /* eslint-enable */
                            )
                        )(action.payload)
                    ),
                );
